import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api';

const STORAGE_KEY = 'auxtechna-tenant-branding';
const PRODUCT_NAME = 'MediCase';

// Pre-API-response default. Keep tenantType empty so the App's render
// fallback doesn't accidentally treat an admin or unknown-tenant host as
// the central-students marketing tenant before the real /api/tenant/me
// response lands. The real tenant overwrites this within a few hundred ms.
const FALLBACK_TENANT = {
  id: '',
  slug: '',
  displayName: '',
  tenantType: '',
  brandPrimaryColor: '',
  brandSecondaryColor: '',
  brandLogoUrl: '',
  brandLoginTagline: '',
};

const TenantBrandingContext = createContext({
  tenant: FALLBACK_TENANT,
  loading: false,
  error: null,
});

function readCached() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_err) {
    return null;
  }
}

function writeCached(tenant) {
  try {
    if (!tenant || !tenant.slug) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tenant));
  } catch (_err) {
    // localStorage may be disabled (Safari private mode etc.) — non-fatal
  }
}

// Inject CSS custom properties on :root so any --app-primary consumer in
// style.css picks up tenant colours without per-component refactoring.
function applyBrandingToDocument(tenant) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const primary = tenant.brandPrimaryColor || '';
  const secondary = tenant.brandSecondaryColor || '';

  if (primary) {
    root.style.setProperty('--app-primary', primary);
    root.style.setProperty('--bs-primary', primary);
  } else {
    root.style.removeProperty('--app-primary');
    root.style.removeProperty('--bs-primary');
  }

  if (secondary) {
    root.style.setProperty('--app-secondary', secondary);
  } else {
    root.style.removeProperty('--app-secondary');
  }

  // Update <title> to include tenant display name when available
  if (tenant.displayName) {
    document.title = `${tenant.displayName} | ${PRODUCT_NAME}`;
  } else {
    document.title = PRODUCT_NAME;
  }
}

export function TenantBrandingProvider({ children }) {
  const cached = readCached();
  const [tenant, setTenant] = useState(cached || FALLBACK_TENANT);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  // Apply cached branding immediately to avoid a flash of unbranded styling
  useEffect(() => {
    applyBrandingToDocument(tenant);
  }, [tenant]);

  const fetchTenant = useCallback(async () => {
    try {
      const response = await api.getTenantInfo();
      const next = response?.tenant || FALLBACK_TENANT;
      setTenant(next);
      writeCached(next);
      setError(null);
      return next;
    } catch (err) {
      // Non-fatal — fall back to cache or defaults.
      setError(err?.message || 'Failed to load tenant branding');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch fresh branding from the server on every load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await fetchTenant();
      if (cancelled) return;
      // setTenant already called inside fetchTenant; just suppress re-set if cancelled
      if (!next) {
        // failed silently; cache fallback already applied above
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchTenant]);

  // Apply an optimistic update from the tenant-settings save flow so brand
  // colours / display name swap in immediately without a refetch round trip.
  const applyTenantUpdate = useCallback((next) => {
    if (!next) return;
    const merged = { ...tenant, ...next };
    setTenant(merged);
    writeCached(merged);
  }, [tenant]);

  const value = useMemo(
    () => ({ tenant, loading, error, refreshTenant: fetchTenant, applyTenantUpdate }),
    [tenant, loading, error, fetchTenant, applyTenantUpdate]
  );

  return (
    <TenantBrandingContext.Provider value={value}>
      {children}
    </TenantBrandingContext.Provider>
  );
}

export function useCurrentTenant() {
  return useContext(TenantBrandingContext);
}
